import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Box, Card, CardContent, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { colors } from '~/theme';
import { dicasCards } from './dicas.content';
import { DICA_MOCKUPS } from './dicas.mockups';

interface TipsPanelProps {
  showScrollIndicator?: boolean;
  stickyBottomOffset?: number;
}

const getScrollContainer = (element: HTMLElement): Element => {
  let node = element.parentElement;

  while (node) {
    if (['auto', 'scroll'].includes(getComputedStyle(node).overflowY)) {
      return node;
    }
    node = node.parentElement;
  }

  return document.scrollingElement ?? document.documentElement;
};

export const TipsPanel = ({ showScrollIndicator = false, stickyBottomOffset = 0 }: TipsPanelProps) => {
  const lastCardRef = useRef<HTMLDivElement | null>(null);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);

  useEffect(() => {
    if (!showScrollIndicator || !lastCardRef.current) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => setHasReachedEnd(entry.isIntersecting), {
      threshold: 0.5,
    });
    observer.observe(lastCardRef.current);

    return () => observer.disconnect();
  }, [showScrollIndicator]);

  return (
    <Stack spacing={2}>
      <Typography variant="h1" fontWeight={700} sx={{ fontSize: '20px' }}>
        DICAS DE SEGURANÇA:
      </Typography>
      <Stack spacing={2}>
        {dicasCards.map((card, index) => (
          <Card
            key={card.title}
            variant="outlined"
            ref={index === dicasCards.length - 1 ? lastCardRef : undefined}
          >
            <CardContent>
              <Stack spacing={1.5}>
                {DICA_MOCKUPS[card.mockup]}
                <Typography variant="h2" fontWeight={700} sx={{ fontSize: '16px', color: colors.neutralBluish600 }}>
                  {card.title}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.neutralBluish600 }}>
                  {card.description}
                </Typography>
                {card.officialUrls?.map(({ label, url }) => (
                  <Typography key={url} variant="body2" sx={{ color: colors.neutralBluish600 }}>
                    <Typography component="span" variant="body2" fontWeight={700} sx={{ color: colors.neutralBluish600 }}>
                      {label}
                    </Typography>{' '}
                    {url.replace('cub.com.br', '')}
                    <Typography component="span" variant="body2" sx={{ color: colors.neutralBluish600, textDecoration: 'underline' }}>
                      cub.com.br
                    </Typography>
                  </Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
      {showScrollIndicator && !hasReachedEnd && (
        <Box
          sx={{
            position: 'sticky',
            bottom: stickyBottomOffset,
            display: 'flex',
            justifyContent: 'center',
            pt: 3,
            background: `linear-gradient(to bottom, transparent, ${colors.white} 60%)`,
          }}
        >
          <IconButton
            aria-label="Rolar para ver mais dicas"
            onClick={(event) => {
              const container = getScrollContainer(event.currentTarget);
              container.scrollBy({ top: container.clientHeight * 0.85, behavior: 'smooth' });
            }}
            sx={{ color: colors.darkGray }}
          >
            <ExpandMoreRoundedIcon />
          </IconButton>
        </Box>
      )}
    </Stack>
  );
};
